import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import supabase from '../../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/ui/Button';

const ResourceList = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchResources = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('resources_1779569461628')
      .select('*')
      .order('created_at', { ascending: false });

    if (!error) setResources(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchResources();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this resource? This action cannot be undone.')) {
      const { error } = await supabase
        .from('resources_1779569461628')
        .delete()
        .eq('id', id);

      if (!error) {
        setResources(resources.filter(r => r.id !== id));
      } else {
        alert('Error deleting resource: ' + error.message);
      }
    }
  };

  const togglePublish = async (resource) => {
    const { error } = await supabase
      .from('resources_1779569461628')
      .update({ published: !resource.published })
      .eq('id', resource.id);

    if (!error) {
      setResources(resources.map(r => 
        r.id === resource.id ? { ...r, published: !r.published } : r
      ));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-brand-navy">Resources</h1>
          <p className="text-sm text-gray-500">Manage your articles, guides, and thinking.</p>
        </div>
        <Button to="/admin/resources/new" variant="primary">
          <SafeIcon icon={FiIcons.FiPlus} className="mr-2" />
          New Resource
        </Button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Title & Slug</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <SafeIcon icon={FiIcons.FiLoader} className="animate-spin inline-block text-brand-teal w-6 h-6" />
                  </td>
                </tr>
              ) : resources.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    No resources found. Create your first one to get started!
                  </td>
                </tr>
              ) : (
                resources.map((resource) => (
                  <tr key={resource.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-brand-navy truncate max-w-xs">{resource.title}</div>
                      <div className="text-xs text-gray-400">/{resource.slug}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="bg-brand-teal/10 text-brand-teal px-2 py-1 rounded text-xs font-medium">
                        {resource.category || 'General'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button 
                        onClick={() => togglePublish(resource)}
                        className={`flex items-center gap-1.5 text-xs font-bold transition-colors ${resource.published ? 'text-green-600' : 'text-gray-400'}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${resource.published ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                        {resource.published ? 'Published' : 'Draft'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(resource.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right space-x-1">
                      <Link 
                        to={`/admin/resources/edit/${resource.id}`}
                        className="inline-flex p-2 text-gray-400 hover:text-brand-teal hover:bg-brand-teal/5 rounded-lg transition-all"
                        title="Edit"
                      >
                        <SafeIcon icon={FiIcons.FiEdit2} />
                      </Link>
                      <button 
                        onClick={() => handleDelete(resource.id)}
                        className="inline-flex p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                        title="Delete"
                      >
                        <SafeIcon icon={FiIcons.FiTrash2} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ResourceList;